import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import styles from "./styles.module.scss";
import SaveBar from "../../Sessions/SaveBar";
import FormInput from "app/components/Programs/FormInput";
import Label from "app/components/Programs/Label";
import PdfUploader from "app/components/Programs/ImageContainers/PdfUploader";
import useFlyerAttachment from "app/components/Programs/ImageContainers/PdfUploader/useFlyerAttachment";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import PaymentMethodSelector from "app/components/PaymentMethodSelector";
import StripeAccountSelector from "../Pricing/StripeAccountSelector";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { ProgramState } from "app/reducers/scheduling/program";
import { resetProgramSettings } from "app/actions/scheduling/program";
import { useHistory } from "react-router-dom";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import { generateCurrencySymbol, privacy_options } from "../Pricing/utils";
import ButtonsContainer from "app/components/Programs/Buttons/ButtonsContainer";
import { FREE_CLINICS_ENABLED } from "../featureFlags";
import { isAcademyOnboardingCoach } from "app/utils/subscription";

const generatePriceLabel = (currency: string, t) => {
  const currencyLabel = currency?.toUpperCase() !== generateCurrencySymbol(currency)
    ? `(${currency?.toUpperCase()})` : '';

  return `${t('Price')} ${currencyLabel}`;
};

const RecurringClinicsOptions = () => {
  const { t } = useTranslation();
  const programState = useSelector((state: RootState): ProgramState => state.program);
  const dispatch = useDispatch();
  const history = useHistory();
  const group = programState?.recurrenceGroup;
  const priceCurrency = group?.priceCurrency;

  const [isFree, setIsFree] = useState<boolean>(group?.free ?? false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const flyer = useFlyerAttachment(group?.flyerPdfUrl);

  const initialCollectType = group?.collectType;
  const [collectOnline, setCollectOnline] = useState<boolean>(
    initialCollectType === "online" || initialCollectType === "both",
  );
  const [collectOffline, setCollectOffline] = useState<boolean>(
    !initialCollectType || initialCollectType === "offline" || initialCollectType === "both",
  );
  const [onlineDefault, setOnlineDefault] = useState<boolean>(group?.onlineDefault ?? false);
  // Lives on each clinic like capacity. Null when the list page didn't carry it,
  // so a save never sends a privacy the coach wasn't shown.
  const [privacy, setPrivacy] = useState(
    privacy_options.find((option) => option.value === programState?.privacy) || null,
  );

  const currentFacility = useCurrentFacility();
  const hasFacilityLevelStripe =
    currentFacility?.data?.featureFlags?.facilityLevelStripeIntegration === true;

  const allCoachOptions = React.useMemo(() => {
    const list = Array.isArray(programState?.coaches) ? programState.coaches : [];

    return list.map((c: any) => ({
      id: c.id,
      value: c.id,
      label: c.fullName || `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim(),
      profilePicture: c.profilePicture || c.avatar,
      allowsPayments: c?.allowsPayments === true,
    }));
  }, [programState?.coaches]);

  const stripeEnabledCoaches = React.useMemo(
    () => allCoachOptions.filter((c) => c.allowsPayments),
    [allCoachOptions],
  );
  const self = useSelector((state: RootState) => state.user.self);
  // Academy onboarding analytics only, and only once the facility has loaded
  // (see Pricing: the field is reported once, with the Stripe status of that
  // moment).
  const paymentTracking =
    isAcademyOnboardingCoach(self) && !currentFacility.pending
      ? {
          objectType: "program" as const,
          stripeConnected: hasFacilityLevelStripe
            ? Boolean(currentFacility.data.stripeIntegrationEnabled)
            : stripeEnabledCoaches.length > 0,
        }
      : undefined;

  const methods = useForm({
    defaultValues: {
      title: group?.title || "",
      subtitle: group?.subtitle || "",
      short_description: group?.shortDescription || "",
      long_description: group?.longDescription || "",
      price: group?.price || "",
      // Lives on each clinic rather than the group, so it comes from the program
      // state the list page filled in, not from `group`.
      capacity: programState?.capacity ?? "",
      stripe_account_id: group?.paymentCollectorCoachProfileId || null,
    },
    mode: "onChange",
  });

  const { handleSubmit } = methods;

  const isOnlineDefault =
    (collectOnline && !collectOffline) ||
    (collectOnline && collectOffline && onlineDefault);

  const handleBack = () => {
    dispatch(resetProgramSettings());
    history.push("/programs");
  };

  const onSubmit = async (data: any) => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    try {
      const collectType =
        collectOnline && collectOffline ? "both" : collectOnline ? "online" : "offline";

      const payload: Record<string, unknown> = {
        title: data.title,
        subtitle: data.subtitle,
        short_description: data.short_description,
        long_description: data.long_description,
        is_free: isFree,
        price: isFree ? 0 : (parseFloat(data.price) || 0),
      };

      // CN-2843: the backend mirrors this across the series, like the flyer.
      // Omitted rather than sent as null when the field holds nothing, so an
      // incomplete state reads as "unchanged" instead of failing validation.
      const capacity = parseInt(data.capacity, 10);
      if (!Number.isNaN(capacity)) {
        payload.capacity = capacity;
      }

      if (privacy) {
        payload.privacy = privacy.value;
      }

      if (!isFree) {
        payload.collect_type = collectType;
        payload.online_default = isOnlineDefault;
      }
      // Free clinics keep a creator-chosen collector too — the $0 registrations
      // are recorded under it. Only send the key when the field is shown.
      if (!hasFacilityLevelStripe) {
        payload.payment_collector_coach_profile_id = data.stripe_account_id;
      }

      // CN-2693: the flyer is stored per clinic, so the backend mirrors this
      // across the series. Omitted entirely when the coach didn't touch it.
      const flyerPdfUrl = await flyer.resolveUrl();
      if (flyerPdfUrl !== undefined) {
        payload.flyer_pdf_url = flyerPdfUrl;
      }

      await schedulingFetchPatch(`/programs/${programState.id}/update_recurrence_group`, payload);

      toast.success(t("Recurring clinics updated successfully"));
      dispatch(resetProgramSettings());
      history.push("/programs");
    } catch (error) {
      console.error("Error updating recurring clinics:", error);
      toast.error(t("Failed to update recurring clinics"));
      setSubmitting(false);
    }
  };

  return (
    <div className={`${styles.container} programs-page-container`}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_fields_container}>
            <div className={styles.form_row}>
              <div className={styles.form_field}>
                <FormInput
                  tagTitle={t("Title")}
                  fieldName="title"
                  placeholder={t("Enter title")}
                  isRequired={true}
                />
              </div>

              <div className={styles.form_field}>
                <FormInput
                  tagTitle={t("Subtitle")}
                  fieldName="subtitle"
                  placeholder={t("Enter subtitle")}
                />
              </div>
            </div>

            <div className={styles.form_row}>
              <div className={styles.form_field}>
                <FormInput
                  tagTitle={t("Short Description")}
                  fieldName="short_description"
                  placeholder={t("Enter short description")}
                  multiline={true}
                />
              </div>

              <div className={styles.form_field}>
                <FormInput
                  tagTitle={t("Long Description")}
                  fieldName="long_description"
                  placeholder={t("Enter long description")}
                  multiline={true}
                />
              </div>
            </div>

            <div className={styles.form_row}>
              <div className={styles.form_field}>
                <Label
                  text={t("Clinic Flyer")}
                  item={(
                    <PdfUploader
                      onFileSelect={flyer.onFileSelect}
                      onFileRemove={flyer.onFileRemove}
                      fileName={flyer.fileName}
                    />
                  )}
                />
              </div>
              <div className={styles.form_field}>
                <FormInput
                  tagTitle={t("Capacity")}
                  placeholder={t("Max Number of Athletes")}
                  fieldName="capacity"
                  isNumberOnly={true}
                  // A seat count, so a typed "12.9" is rejected rather than
                  // quietly becoming 12 at parseInt.
                  onlyInteger={true}
                  isRequired={true}
                />
              </div>
            </div>

            {privacy && (
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <Label
                    text={t("Privacy")}
                    required={true}
                    item={(
                      <ButtonsContainer
                        customStyle={styles.privacy_item}
                        items={privacy_options}
                        containerStyle={styles.option_button_group}
                        openCalendarSelected={privacy}
                        setOpenCalendarSelected={setPrivacy}
                      />
                    )}
                  />
                </div>
                <div className={styles.form_field}></div>
              </div>
            )}

            {FREE_CLINICS_ENABLED && (
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <SwitchToggle
                    checked={isFree}
                    onChange={setIsFree}
                    title={t("Free Clinic")}
                  />
                </div>
                <div className={styles.form_field}></div>
              </div>
            )}

            {!isFree && (
              <>
                <div className={styles.form_row}>
                  <div className={styles.form_field}>
                    <FormInput
                      tagTitle={generatePriceLabel(priceCurrency, t)}
                      fieldName="price"
                      isRequired={true}
                      addDollar={true}
                      currency={{ currency: generateCurrencySymbol(priceCurrency) }}
                      onlyInteger={false}
                    />
                  </div>
                  <div className={styles.form_field}></div>
                </div>

                <div className={styles.form_row}>
                  <div className={styles.form_field}>
                    <PaymentMethodSelector
                      collectOnline={collectOnline}
                      setCollectOnline={setCollectOnline}
                      collectOffline={collectOffline}
                      setCollectOffline={setCollectOffline}
                      onlineDefault={onlineDefault}
                      setOnlineDefault={setOnlineDefault}
                      tracking={paymentTracking}
                    />

                    {!hasFacilityLevelStripe && (
                      <StripeAccountSelector
                        control={methods.control}
                        stripeCoachOptions={allCoachOptions}
                        stripeEnabledCoaches={stripeEnabledCoaches}
                        collectOnline={collectOnline}
                        canEditPaymentCollector={true}
                        durationCustomStyles={{}}
                        tooltipOrientation="left"
                      />
                    )}
                  </div>
                  <div className={styles.form_field}></div>
                </div>
              </>
            )}
            {/* Free clinics collect no payment, but the creator still picks the
                coach the registrations are attributed to (their Reports). */}
            {isFree && !hasFacilityLevelStripe && (
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <StripeAccountSelector
                    control={methods.control}
                    stripeCoachOptions={allCoachOptions}
                    stripeEnabledCoaches={stripeEnabledCoaches}
                    collectOnline={false}
                    canEditPaymentCollector={true}
                    durationCustomStyles={{}}
                    isFree
                    tooltipOrientation="left"
                  />
                </div>
                <div className={styles.form_field}></div>
              </div>
            )}
          </div>

          <SaveBar
            onCancel={handleBack}
            onSubmit={handleSubmit(onSubmit)}
            submitLabel={submitting ? t('Saving...') : t('Save')}
            submitDisabled={submitting}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default RecurringClinicsOptions;
